import { useEffect, useRef, useState } from 'react';

const features = [
  {
    icon: 'ri-book-open-line',
    title: '海量漫画库',
    desc: '天天漫画收录超过10万部漫画资源，涵盖日漫、国漫、韩漫等全品类。从经典连载到热门新作，每日更新上千章节，确保你永远不会漫荒。',
    image: '/images/features/feat-1.webp',
  },
  {
    icon: 'ri-lightbulb-flash-line',
    title: 'AI 智能推荐',
    desc: '基于深度学习的推荐算法，天天漫画能够精准分析你的阅读偏好，越用越懂你。为你量身推荐适合口味的作品，每一次打开都有新惊喜。',
    image: '/images/features/feat-2.webp',
  },
  {
    icon: 'ri-download-cloud-line',
    title: '极速离线缓存',
    desc: '支持整本漫画一键下载，Wi-Fi 环境下自动缓存最新章节。天天漫画的离线阅读功能让你在没有网络时也能畅快阅读，地铁、飞机、出差都不怕。',
    image: '/images/features/feat-3.webp',
  },
  {
    icon: 'ri-palette-line',
    title: '个性化阅读体验',
    desc: '多种阅读模式自由切换：横屏/竖屏、翻页/滚动、日/夜间模式。亮度调节、背景色自定义、字体大小随心调，打造完全属于你的阅读空间。',
    image: '/images/features/feat-4.webp',
  },
  {
    icon: 'ri-community-line',
    title: '漫友互动社区',
    desc: '加入天天漫画的社区，与同好一起讨论剧情、分享推荐、参与话题。在这里你可以找到志同道合的漫友，发现更多被埋没的宝藏作品。',
    image: '/images/features/feat-5.webp',
  },
  {
    icon: 'ri-device-line',
    title: '多端无缝同步',
    desc: '支持手机、平板、电脑多端登录，阅读进度云端实时同步。换设备登录后自动恢复书签，让阅读体验在不同设备间无缝衔接。',
    image: '/images/features/feat-6.webp',
  },
];

function ScrollReveal({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.unobserve(el); } },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}>
      {children}
    </div>
  );
}

export default function Features() {
  return (
    <div className="min-h-screen bg-white pt-20">
      <section className="px-4 md:px-8 lg:px-16 xl:px-24 py-20 md:py-28">
        <div className="max-w-6xl mx-auto">
          <ScrollReveal>
            <div className="text-center mb-20">
              <span className="inline-block px-4 py-1.5 rounded-full bg-pink-50 text-pink-600 text-sm font-medium mb-4">
                功能介绍
              </span>
              <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                天天漫画功能亮点
              </h1>
              <p className="text-gray-500 text-lg max-w-2xl mx-auto">
                天天漫画APP下载后，即可解锁六大核心功能，每一款都经过精心打磨，只为给你极致的漫画阅读体验。
              </p>
            </div>
          </ScrollReveal>

          <div className="space-y-24 md:space-y-32">
            {features.map((feature, index) => (
              <ScrollReveal key={feature.title}>
                <div className={`grid lg:grid-cols-2 gap-10 md:gap-16 items-center ${index % 2 === 1 ? 'lg:grid-flow-dense' : ''}`}>
                  <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                    <div className="relative rounded-xl overflow-hidden aspect-[16/10] border border-gray-100">
                      <img src={feature.image} alt={feature.title} className="w-full h-full object-cover" />
                    </div>
                  </div>
                  <div className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                    <div className="w-12 h-12 rounded-xl bg-pink-500 flex items-center justify-center mb-6">
                      <i className={`${feature.icon} text-xl text-white`} />
                    </div>
                    <h2 className="font-serif text-2xl md:text-3xl font-bold text-gray-900 mb-4">{feature.title}</h2>
                    <p className="text-gray-500 leading-relaxed mb-6">{feature.desc}</p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}